import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api, { getConfigs } from "../services/api";
import {
    activeExamConfigs,
    normalizeCertCode,
} from "../utils/certCatalog";

const CertContext = createContext(null);

export const configsToCertList = (configs = {}) => Object.values(configs).map(config => ({
    name: config.name || config.id,
    id: config.id,
    code: config.id,
    vendor: config.vendor || config.provider,
    category: config.category || config.level,
    provider: config.provider || config.vendor,
}));

export function CertProvider({ children, preferredCert = "" }) {
    const hadPersistedCertRef = useRef(Boolean(localStorage.getItem("activeCert")));
    const [activeCert, setActiveCert] = useState(() => normalizeCertCode(localStorage.getItem("activeCert") || ""));
    const [CERTS, setCerts] = useState([]);
    const [EXAM_CONFIGS, setExamConfigs] = useState({});
    const [loading, setLoading] = useState(true);
    const [configError, setConfigError] = useState(null);
    const [catalogReady, setCatalogReady] = useState(false);
    const [configReload, setConfigReload] = useState(0);
    const [isChangingCert, setIsChangingCert] = useState(false);
    const [examDate, setExamDate] = useState(null);
    const activeCertRef = useRef(activeCert);
    const examDateRef = useRef(null);
    const dateRequestRef = useRef(0);
    const certChangeTimerRef = useRef(null);
    const preferredCertRef = useRef(preferredCert);
    preferredCertRef.current = preferredCert;

    // examDate is populated by AppDataContext from the dashboard response.
    // No separate fetch needed here.

    const setExamDateRaw = useCallback((newDate) => {
        // A dashboard refresh is authoritative and invalidates any older
        // optimistic mutation that may still be in flight.
        dateRequestRef.current += 1;
        examDateRef.current = newDate;
        setExamDate(newDate);
    }, []);

    const updateExamDate = async (newDate) => {
        const userId = localStorage.getItem("userId");
        if (!userId || !activeCert) return;
        const previousDate = examDateRef.current;
        const certAtRequest = activeCert;
        const requestId = ++dateRequestRef.current;
        examDateRef.current = newDate;
        setExamDate(newDate); // Optimistic update
        try {
            await api.post(`/api/users/${userId}/exam-date`, { examDate: newDate, cert_code: certAtRequest });
        } catch (err) {
            // Do not let an older failure roll back a newer selection or a
            // dashboard refresh. Only the latest optimistic write may revert.
            if (requestId === dateRequestRef.current && certAtRequest === activeCertRef.current) {
                examDateRef.current = previousDate;
                setExamDate(previousDate);
            }
            console.error("Failed to persist exam date:", err);
            throw err;
        }
    };

    const setAndPersistCert = useCallback((cert, { showTransition = true, userSelected = true } = {}) => {
        const normalized = normalizeCertCode(cert);
        if (!normalized) return;
        // The account target is a bootstrap default. Once the learner chooses
        // a certification, a late profile or catalogue load must honor it.
        if (userSelected) hadPersistedCertRef.current = true;
        if (normalized === activeCert) return;
        if (showTransition) setIsChangingCert(true);
        activeCertRef.current = normalized;
        setActiveCert(normalized);
        localStorage.setItem("activeCert", normalized);
        setExamDateRaw(null);

        if (certChangeTimerRef.current) clearTimeout(certChangeTimerRef.current);
        if (showTransition) {
            // AppDataContext normally dismisses this when the new snapshot is
            // ready. Keep the selector usable if that request exhausts retries.
            certChangeTimerRef.current = setTimeout(() => setIsChangingCert(false), 5000);
        }
    }, [activeCert, setExamDateRaw]);

    useEffect(() => {
        activeCertRef.current = activeCert;
    }, [activeCert]);

    useEffect(() => {
        let current = true;
        const fetchConfigs = async () => {
            setLoading(true);
            setConfigError(null);
            try {
                const data = await getConfigs();
                if (!current) return;
                // The server's active set is authoritative. In particular, a
                // successful all-inactive response must stay empty.
                const selectableConfigs = activeExamConfigs(data);
                const certList = configsToCertList(selectableConfigs);
                setExamConfigs(selectableConfigs);
                setCerts(certList);
                setCatalogReady(true);

                // Migration: If savedCert is a long name, try to find the code for it
                const savedCert = localStorage.getItem("activeCert");
                const preferred = normalizeCertCode(preferredCertRef.current);
                const normalizedSaved = normalizeCertCode(savedCert || "");
                const savedByCode = savedCert && selectableConfigs[normalizedSaved];
                const savedByName = savedCert && Object.values(selectableConfigs).find(c => c.name === savedCert);
                const validSavedCert = savedByCode ? normalizedSaved : savedByName?.id;
                // A retired/unknown browser value is not an intentional current
                // choice. Treat it as absent so the account profile can still
                // select its valid target certification when that arrives.
                if (savedCert && !validSavedCert) hadPersistedCertRef.current = false;
                const preferredAvailable = !hadPersistedCertRef.current && selectableConfigs[preferred];
                if (savedCert) {
                    if (validSavedCert) {
                        setActiveCert(validSavedCert);
                        localStorage.setItem("activeCert", validSavedCert);
                    } else {
                        const defaultCert = preferredAvailable ? preferred : (certList[0]?.id || "");
                        setActiveCert(defaultCert);
                        if (defaultCert) localStorage.setItem("activeCert", defaultCert);
                        else localStorage.removeItem("activeCert");
                    }
                } else {
                    const defaultCert = preferredAvailable ? preferred : (certList[0]?.id || "");
                    setActiveCert(defaultCert);
                    if (defaultCert) localStorage.setItem("activeCert", defaultCert);
                }
            } catch (err) {
                if (!current) return;
                console.error("Failed to load exam configurations:", err);
                // Activity is server-owned. A failed first request must not
                // advertise a hardcoded list that may include retired exams.
                // If a live snapshot was loaded earlier, leave that snapshot
                // intact while exposing this retryable refresh failure.
                setConfigError(err);
            } finally {
                if (current) setLoading(false);
            }
        };
        fetchConfigs();
        return () => { current = false; };
    // Account preferences select from this catalogue in the effect below.
    // Refetching it when the profile arrives would restart data bootstrap and
    // remount Analytics while its first audit request is still in flight.
    }, [configReload]);

    useEffect(() => () => {
        if (certChangeTimerRef.current) clearTimeout(certChangeTimerRef.current);
    }, []);

    // The account profile is fetched by App while configurations load. If this
    // browser had no explicit prior selection, adopt that account target as
    // soon as both pieces are known instead of permanently keeping the first
    // alphabetic/default certification chosen during bootstrap.
    useEffect(() => {
        const preferred = normalizeCertCode(preferredCert);
        if (!loading && !hadPersistedCertRef.current && EXAM_CONFIGS[preferred] && activeCert !== preferred) {
            setAndPersistCert(preferred, { showTransition: false, userSelected: false });
        }
    }, [EXAM_CONFIGS, activeCert, loading, preferredCert, setAndPersistCert]);

    return (
        <CertContext.Provider value={{
            activeCert,
            setActiveCert: setAndPersistCert,
            CERTS,
            EXAM_CONFIGS,
            loading,
            catalogReady,
            configError,
            retryConfigs: () => setConfigReload(value => value + 1),
            isChangingCert,
            setIsChangingCert,
            examDate,
            setExamDate: updateExamDate,
            _setExamDateRaw: setExamDateRaw,
        }}>
            {children}
        </CertContext.Provider>
    );
}

export function useCert() {
    const ctx = useContext(CertContext);
    if (!ctx) throw new Error("useCert must be used inside CertProvider");
    return ctx;
}
